import { dark, light } from '@london-life/game/house/room';
import { legs, row, type Draw } from './common';

// Set pieces for outside, transport and industry: the river, parks and farms, stations, the airport, the
// cable car, the boats, the refinery and the building plots.

const STEEL = '#9aa3a8';

export const OUTDOOR: Record<string, Draw> = {
  // ---------- water, parks and gardens
  river(k, { w, d, c }, g) {
    // the Thames along one side: the water just over the ground, ripples that catch the light, a stone edge
    k.box(w, 0.04, d, dark(c, 0.8), 0, 0.02, 0, g);
    k.lit(w, 0.01, d * 0.96, c, 0, 0.045, 0, g, 0.22);
    for (let i = 0; i < Math.round(w); i += 1)
      k.box(
        0.6,
        0.012,
        0.05,
        light(c, 0.5),
        -w / 2 + 0.5 + i * 1.03,
        0.055,
        (((i * 37) % 10) / 10) * d * 0.9 - d * 0.45,
        g,
      );
    k.box(w, 0.16, 0.22, '#a9a196', 0, 0.08, d / 2 + 0.11, g);
  },
  railings(k, { w, h, c }, g) {
    k.box(w, 0.05, 0.05, c, 0, h, 0, g);
    k.box(w, 0.04, 0.04, c, 0, h * 0.5, 0, g);
    for (let i = 0; i <= Math.round(w / 0.25); i += 1) k.box(0.025, h, 0.025, c, -w / 2 + i * 0.25, h / 2, 0, g);
  },
  pond(k, { w, d, c }, g) {
    k.cylinder(Math.min(w, d) / 2 + 0.1, 0.12, '#a9a196', 0, 0.06, 0, g, Math.min(w, d) / 2 + 0.1, 20).scale.set(
      1,
      1,
      d / w,
    );
    const water = k.cylinder(Math.min(w, d) / 2, 0.02, c, 0, 0.11, 0, g, Math.min(w, d) / 2, 20);
    water.scale.set(1, 1, d / w);
    for (const [x, z] of [
      [-0.25, 0.1],
      [0.2, -0.15],
    ] as const)
      k.cylinder(0.12, 0.02, '#4f8a4b', x * w, 0.125, z * d, g, 0.12, 8);
  },
  fountain(k, { w, h, c }, g) {
    k.cylinder(w / 2, 0.4, '#c7bfb2', 0, 0.2, 0, g, w / 2, 16);
    k.cylinder(w / 2 - 0.12, 0.02, c, 0, 0.39, 0, g, w / 2 - 0.12, 16);
    k.cylinder(0.12, h * 0.6, '#c7bfb2', 0, h * 0.3, 0, g);
    k.cylinder(w * 0.22, 0.12, '#c7bfb2', 0, h * 0.62, 0, g, w * 0.22, 12);
    k.cylinder(0.05, h * 0.35, light(c, 0.5), 0, h * 0.8, 0, g, 0.02);
  },
  lattice(k, { w, d, h, c }, g) {
    // the roof garden's timber roof: ribs arching overhead at the back, posts down to the floor
    const ribs = Math.max(2, Math.round(w / 1.6));
    for (let i = 0; i < ribs; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / ribs;
      k.box(0.1, h, 0.14, c, x, h / 2, -d / 2, g);
      const beam = k.box(0.1, 0.14, d * 0.7, c, x, h - 0.1, -d * 0.15, g);
      beam.rotation.x = -0.18;
    }
    for (const z of [-d / 2, -d / 4]) k.box(w, 0.1, 0.1, dark(c, 0.85), 0, h + 0.05 - (z + d / 2) * 0.15, z, g);
  },
  arch(k, { w, h, c, label }, g) {
    // two posts and a board with the place's name: the way in to a park or a market
    for (const x of [-1, 1]) k.box(0.22, h, 0.22, c, (x * w) / 2, h / 2, 0, g);
    k.box(w + 0.4, 0.5, 0.15, c, 0, h - 0.1, 0, g);
    if (label) k.sign(label, 0, h - 0.1, 0.08, g, Math.min(w, 3.2), '#fffdf5', c);
  },
  tunneldome(k, { w, h, c }, g) {
    // the Greenwich foot tunnel's round brick building with its glass dome
    k.cylinder(w / 2, h * 0.6, c, 0, (h * 0.6) / 2, 0, g, w / 2, 16);
    k.dome(w / 2, '#9fd3f2', 0, h * 0.6, 0, g);
    k.cylinder(w / 2 + 0.05, 0.1, light(c, 0.4), 0, h * 0.6, 0, g, w / 2 + 0.05, 16);
    k.box(0.8, 1.7, 0.1, '#3d2a1e', 0, 0.85, w / 2 - 0.02, g);
    k.sphere(0.1, '#c9a227', 0, h * 0.6 + w / 2, 0, g);
  },
  pen(k, { w, d, c, variant }, g) {
    // a fenced pen with straw on the ground and its animals
    k.box(w, 0.02, d, '#c9b37a', 0, 0.01, 0, g);
    for (const [x, z, long, across] of [
      [0, -d / 2, w, true],
      [0, d / 2, w, true],
      [-w / 2, 0, d, false],
      [w / 2, 0, d, false],
    ] as const) {
      for (const y of [0.35, 0.7]) k.box(across ? long : 0.05, 0.06, across ? 0.05 : long, c, x, y, z, g);
      for (let i = 0; i <= Math.round(long / 0.9); i += 1) {
        const at = -long / 2 + (long * i) / Math.round(long / 0.9);
        k.box(0.08, 0.85, 0.08, dark(c, 0.8), across ? at : x, 0.42, across ? z : at, g);
      }
    }
    const kind = variant ?? 'sheep';
    const coat = kind === 'llamas' ? '#e7d3b0' : kind === 'pigs' ? '#f2b8b0' : kind === 'goats' ? '#d9cfc0' : '#f4f1ea';
    const face = kind === 'pigs' ? '#e89a90' : kind === 'sheep' ? '#2a2d31' : '#8a6a4a';
    const count = Math.max(1, Math.round((w * d) / 2.2));
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + 0.5 + ((i * 0.97) % Math.max(0.5, w - 1));
      const z = -d / 2 + 0.5 + ((i * 1.31) % Math.max(0.5, d - 1));
      const turn = (i * 1.7) % (Math.PI * 2);
      k.box(0.6, 0.35, 0.32, coat, x, 0.45, z, g, turn);
      const hx = Math.cos(turn) * 0.34,
        hz = -Math.sin(turn) * 0.34;
      if (kind === 'llamas') {
        k.box(0.12, 0.55, 0.12, coat, x + hx, 0.85, z + hz, g);
        k.box(0.16, 0.14, 0.12, face, x + hx * 1.15, 1.15, z + hz * 1.15, g, turn);
      } else k.box(0.16, 0.18, 0.18, face, x + hx, 0.55, z + hz, g, turn);
      for (const [a, b] of [
        [0.2, 0.1],
        [-0.2, 0.1],
        [0.2, -0.1],
        [-0.2, -0.1],
      ] as const)
        k.box(
          0.06,
          0.28,
          0.06,
          face,
          x + Math.cos(turn) * a + Math.sin(turn) * b,
          0.14,
          z - Math.sin(turn) * a + Math.cos(turn) * b,
          g,
        );
    }
  },
  haybales(k, { w, d, c }, g) {
    const bales = Math.max(1, Math.round(w / 1));
    for (let i = 0; i < bales; i += 1) {
      const bale = k.cylinder(0.45, Math.min(d, 0.9), c, -w / 2 + 0.5 + i * 1, 0.45, 0, g, 0.45, 12);
      bale.rotation.x = Math.PI / 2;
    }
    if (bales > 1) {
      const top = k.cylinder(0.45, Math.min(d, 0.9), light(c, 0.1), -w / 2 + 1, 1.2, 0, g, 0.45, 12);
      top.rotation.x = Math.PI / 2;
    }
  },
  barn(k, { w, d, h, c }, g) {
    k.box(w, h * 0.6, d, c, 0, (h * 0.6) / 2, 0, g);
    const roof = k.cone(Math.hypot(w, d) / 2, h * 0.4, '#4b5563', 0, h * 0.8, 0, g, 4, Math.PI / 4);
    roof.scale.set((w / Math.hypot(w, d)) * 1.45, 1, (d / Math.hypot(w, d)) * 1.45);
    k.box(w * 0.4, h * 0.5, 0.05, dark(c, 0.7), 0, h * 0.25, d / 2, g);
    k.box(w * 0.4, 0.06, 0.06, '#fffdf5', 0, h * 0.25, d / 2 + 0.03, g, 0.9);
    k.box(w * 0.4, 0.06, 0.06, '#fffdf5', 0, h * 0.25, d / 2 + 0.03, g, -0.9);
  },
  tractor(k, { w, d, h, c }, g) {
    k.box(w * 0.6, h * 0.35, d * 0.6, c, w * 0.15, h * 0.45, 0, g);
    k.box(w * 0.35, h * 0.45, d * 0.65, light(c, 0.15), -w * 0.18, h * 0.72, 0, g); // cab
    k.box(w * 0.3, h * 0.35, d * 0.6, '#cfe8f2', -w * 0.18, h * 0.78, 0, g);
    for (const z of [-1, 1]) {
      const big = k.cylinder(h * 0.32, 0.25, '#1f2429', -w * 0.25, h * 0.32, (z * d) / 2, g, h * 0.32, 12);
      big.rotation.x = Math.PI / 2;
      const small = k.cylinder(h * 0.18, 0.2, '#1f2429', w * 0.32, h * 0.18, (z * d) / 2.2, g, h * 0.18, 10);
      small.rotation.x = Math.PI / 2;
    }
    k.cylinder(0.04, h * 0.3, '#3d4650', w * 0.3, h * 0.75, 0, g);
  },
  deer(k, { w, c }, g) {
    const count = Math.max(1, Math.round(w / 0.9));
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + 0.45 + i * 0.9,
        turn = i % 2 ? 0.6 : -0.4;
      k.box(0.55, 0.28, 0.24, c, x, 0.65, 0, g, turn);
      k.box(0.1, 0.32, 0.1, c, x + Math.cos(turn) * 0.28, 0.9, -Math.sin(turn) * 0.28, g);
      k.box(0.14, 0.12, 0.2, dark(c, 0.85), x + Math.cos(turn) * 0.33, 1.06, -Math.sin(turn) * 0.33, g, turn);
      if (i % 2 === 0)
        for (const side of [-1, 1])
          k.box(0.02, 0.22, 0.02, '#5b4636', x + Math.cos(turn) * 0.3, 1.24, side * 0.06 - Math.sin(turn) * 0.3, g);
      for (const [a, b] of [
        [0.2, 0.08],
        [-0.2, 0.08],
        [0.2, -0.08],
        [-0.2, -0.08],
      ] as const)
        k.box(
          0.04,
          0.5,
          0.04,
          dark(c, 0.8),
          x + Math.cos(turn) * a + Math.sin(turn) * b,
          0.25,
          -Math.sin(turn) * a + Math.cos(turn) * b,
          g,
        );
    }
  },
  picnicmat(k, { w, d, c }, g) {
    const squares = 4;
    for (let i = 0; i < squares; i += 1)
      for (let j = 0; j < squares; j += 1)
        k.box(
          w / squares,
          0.02,
          d / squares,
          (i + j) % 2 ? c : '#fffdf5',
          -w / 2 + (w * (i + 0.5)) / squares,
          0.01,
          -d / 2 + (d * (j + 0.5)) / squares,
          g,
        );
    k.box(0.45, 0.3, 0.3, '#b07d4a', w * 0.25, 0.17, -d * 0.2, g); // the basket
    k.box(0.04, 0.2, 0.32, '#8a5a3b', w * 0.25, 0.4, -d * 0.2, g);
    k.cylinder(0.04, 0.25, '#7a2b3b', -w * 0.2, 0.14, d * 0.15, g);
  },
  lamppost(k, { h, c }, g) {
    k.cylinder(0.12, 0.3, c, 0, 0.15, 0, g);
    k.cylinder(0.05, h, c, 0, h / 2, 0, g);
    k.box(0.28, 0.35, 0.28, dark(c, 0.8), 0, h + 0.1, 0, g);
    k.glow(0.11, '#ffe2a8', 0, h + 0.1, 0, g);
  },
  barrierpier(k, { w, d, h, c }, g) {
    // a Thames Barrier pier: a concrete base in the water and the shining steel hood, ribbed like a sail
    k.box(w * 0.5, h * 0.35, d, '#a9a196', 0, (h * 0.35) / 2, 0, g);
    const hood = k.dome(w / 2, c, 0, h * 0.35, 0, g);
    hood.scale.set(1, (h * 0.65) / (w / 2), d / w);
    for (const x of [-0.25, 0, 0.25]) {
      const rib = k.box(0.05, h * 0.55, 0.05, light(c, 0.4), x * w, h * 0.62, 0, g);
      rib.rotation.z = -x * 1.8;
    }
  },

  // ---------- transport
  platform(k, { w, d, h, c, label }, g) {
    // the platform edge with its yellow line, the track below, a train at the platform and the tunnel wall
    const trackZ = -d / 2 + d * 0.32;
    k.box(w, 0.25, d * 0.42, '#3b3f44', 0, -0.12, trackZ, g); // the track bed, lower than the floor
    for (const z of [-0.3, 0.3]) k.box(w, 0.04, 0.06, STEEL, 0, 0.02, trackZ + z, g);
    k.box(w, 0.02, 0.12, '#facc15', 0, 0.012, -d / 2 + d * 0.55, g);
    k.box(w, h * 0.8, 0.12, '#e6ebf0', 0, (h * 0.8) / 2, -d / 2, g); // the curved tunnel wall
    k.box(w, 0.35, 0.14, c, 0, h * 0.62, -d / 2 + 0.02, g); // the line's colour band
    if (label) k.sign(label, w * 0.25, h * 0.62, -d / 2 + 0.1, g, 1.6, '#fffdf5', '#1d3c8f');
    // the train
    const carriage = Math.min(w * 0.85, 9);
    k.box(carriage, 1.9, 1.3, '#e9ecef', 0, 1.05, trackZ, g);
    k.box(carriage, 0.25, 1.32, '#c81e1e', 0, 0.4, trackZ, g);
    k.box(carriage, 0.22, 1.32, '#1d3c8f', 0, 1.85, trackZ, g);
    const doors = Math.max(2, Math.round(carriage / 1.8));
    for (let i = 0; i < doors; i += 1) {
      const x = -carriage / 2 + (carriage * (i + 0.5)) / doors;
      k.box(0.7, 1.35, 0.02, '#c81e1e', x, 1.05, trackZ + 0.66, g);
      k.lit(0.55, 0.45, 0.02, '#fff6d6', x + 0.7, 1.35, trackZ + 0.66, g, 0.6);
    }
    k.lit(0.3, 0.12, 0.02, '#f59e0b', carriage / 2 - 0.4, 1.7, trackZ + 0.67, g, 1);
  },
  railplatform(k, { w, d, h, c, label }, g) {
    // a main-line platform under the station's roof: the edge with its yellow line, the track below, a long
    // train at the platform in its line's colour (the band along the windows, a sloping nose), the canopy's
    // iron columns and the platform's number on its sign
    const trackZ = -d / 2 + d * 0.3;
    k.box(w, 0.3, d * 0.5, '#3b3f44', 0, -0.15, trackZ, g);
    for (const z of [-0.36, 0.36]) k.box(w, 0.05, 0.07, STEEL, 0, 0.02, trackZ + z, g);
    k.box(w, 0.02, 0.14, '#facc15', 0, 0.012, -d / 2 + d * 0.58, g);
    const carriage = w * 0.9;
    k.box(carriage, 2.1, 1.5, '#f2f3f5', -w * 0.04, 1.2, trackZ, g);
    k.box(carriage, 0.5, 1.52, c, -w * 0.04, 1.45, trackZ, g);
    k.box(carriage, 0.3, 1.52, '#2b2f3a', -w * 0.04, 0.3, trackZ, g);
    const doors = Math.max(2, Math.round(carriage / 3.2));
    for (let i = 0; i < doors; i += 1) {
      const x = -w * 0.04 - carriage / 2 + (carriage * (i + 0.5)) / doors;
      k.box(0.9, 1.7, 0.02, c, x, 1.05, trackZ + 0.76, g);
      k.lit(1.2, 0.42, 0.02, '#fff6d6', x + 1.2, 1.45, trackZ + 0.765, g, 0.5);
    }
    const nose = k.box(1.2, 1.6, 1.5, c, -w * 0.04 + carriage / 2 + 0.5, 1.0, trackZ, g);
    nose.rotation.z = -0.4;
    k.lit(0.25, 0.12, 0.02, '#fffbe8', carriage / 2 - 0.2, 0.7, trackZ + 0.77, g, 1.4);
    // the canopy: columns along the platform holding the roof's edge
    const columns = Math.max(2, Math.round(w / 4));
    for (let i = 0; i < columns; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / columns;
      k.box(0.16, h, 0.16, '#3d4650', x, h / 2, d / 2 - 0.3, g);
    }
    k.box(w, 0.14, 0.3, '#3d4650', 0, h, d / 2 - 0.3, g);
    k.box(w, 0.06, d, '#cfd9df', 0, h + 0.1, 0, g);
    if (label) {
      k.box(0.06, 0.6, 0.06, '#3d4650', w * 0.3, h - 0.35, d / 2 - 0.3, g);
      k.sign(label, w * 0.3, h - 0.75, d / 2 - 0.25, g, 1.8, '#fffdf5', '#1d2731');
    }
  },
  gates(k, { w, d, c }, g) {
    const gates = Math.max(2, Math.round(w / 0.8));
    for (let i = 0; i <= gates; i += 1) {
      const x = -w / 2 + (w * i) / gates;
      k.box(0.22, 1, d, '#c9cfd3', x, 0.5, 0, g);
      k.box(0.24, 0.04, 0.2, '#facc15', x, 1.02, d * 0.2, g); // card reader
      k.lit(0.1, 0.04, 0.02, '#22c55e', x, 0.9, d / 2 + 0.01, g, 1);
    }
    for (let i = 0; i < gates; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / gates;
      for (const side of [-1, 1]) k.box(0.22, 0.5, 0.03, c, x + side * 0.14, 0.7, 0, g);
    }
  },
  escalator(k, { w, d, h, c }, g) {
    // two escalators side by side, rising away from the camera, with the black handrails
    const lanes = 2;
    for (let i = 0; i < lanes; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / lanes;
      const steps = Math.round(d / 0.3);
      for (let s = 0; s < steps; s += 1)
        k.box(w / lanes - 0.3, 0.04, 0.3, '#5b636b', x, (h * s) / steps, d / 2 - 0.15 - s * 0.3, g);
      for (const side of [-1, 1]) {
        const rail = k.box(0.1, 0.9, d * 1.05, c, x + (side * (w / lanes - 0.15)) / 2, h / 2 + 0.45, 0, g);
        rail.rotation.x = Math.atan2(h, d);
        const belt = k.box(0.08, 0.05, d * 1.05, '#111418', x + (side * (w / lanes - 0.15)) / 2, h / 2 + 0.92, 0, g);
        belt.rotation.x = Math.atan2(h, d);
      }
    }
  },
  roundel(k, { h, c, label }, g) {
    // the Underground roundel on a post: a red ring and a blue bar with the station's name
    k.cylinder(0.05, h, '#3d4650', 0, h / 2, 0, g);
    const ring = k.cylinder(0.45, 0.06, c, 0, h, 0.02, g, 0.45, 24);
    ring.rotation.x = Math.PI / 2;
    const middle = k.cylinder(0.3, 0.07, '#fffdf5', 0, h, 0.03, g, 0.3, 24);
    middle.rotation.x = Math.PI / 2;
    k.box(1.2, 0.2, 0.08, '#1d3c8f', 0, h, 0.06, g);
    if (label) k.sign(label, 0, h, 0.11, g, 1.15, '#fffdf5', '#1d3c8f');
  },
  shelter(k, { w, d, h, c, label }, g) {
    // a bus shelter: a glass back, a roof, a bench and the timetable
    k.glass(w, h, 0.04, 0, h / 2, -d / 2, g);
    for (const x of [-1, 1]) k.box(0.06, h, 0.06, c, (x * w) / 2, h / 2, -d / 2, g);
    k.box(w + 0.2, 0.08, d + 0.2, c, 0, h, 0, g);
    k.box(w * 0.6, 0.06, 0.35, '#7d8790', 0, 0.48, -d / 2 + 0.25, g);
    k.lit(0.5, 0.9, 0.03, '#fff6d6', w / 2 - 0.35, 1.2, -d / 2 + 0.04, g, 0.5);
    if (label) k.sign(label, 0, h - 0.25, -d / 2 + 0.03, g, Math.min(w * 0.7, 1.8), '#fffdf5', c);
  },
  checkin(k, { w, d, c, label }, g) {
    // the check-in desks with screens over them and the bag belt alongside
    const desks = Math.max(1, Math.round(w / 1.5));
    for (let i = 0; i < desks; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / desks;
      k.box(1, 1.05, d * 0.5, c, x - 0.25, 0.52, 0, g);
      k.box(0.5, 0.35, d * 0.5, '#5b636b', x + 0.5, 0.18, 0, g); // the scales and belt
      k.box(0.5, 0.03, d * 0.45, '#111418', x + 0.5, 0.37, 0, g);
      k.box(0.06, 1.4, 0.06, '#3d4650', x - 0.25, 1.75, -d * 0.2, g);
      k.box(0.7, 0.35, 0.05, '#1f2429', x - 0.25, 2.4, -d * 0.2, g);
      k.lit(0.64, 0.28, 0.01, '#1d4ed8', x - 0.25, 2.4, -d * 0.2 + 0.03, g, 0.8);
    }
    k.box(w, 0.4, 0.15, '#4b5563', 0, 0.2, -d / 2 + 0.08, g);
    if (label) k.sign(label, 0, 2.9, -d * 0.2, g, Math.min(w * 0.6, 2.6), '#fffdf5', '#1f2937');
  },
  scanner(k, { w, d, h, c }, g) {
    // the security arch and the x-ray belt beside it
    for (const x of [-1, 1]) k.box(0.2, h, 0.5, c, x * 0.45, h / 2, 0, g);
    k.box(1.1, 0.2, 0.5, c, 0, h, 0, g);
    k.lit(0.6, 0.06, 0.02, '#22c55e', 0, h - 0.05, 0.26, g, 1);
    k.box(w - 1.3, 0.85, d * 0.6, '#c9cfd3', w / 2 - (w - 1.3) / 2, 0.42, 0, g);
    k.box(1, 0.8, d * 0.62, '#4b5563', w / 2 - (w - 1.3) / 2, 1.1, 0, g);
    row(k, 3, w - 1.5, 0.85, 0, g, ['#3a5f8a', '#7a3b1d', '#2f6b3f'], { w: 0.4, h: 0.15, d: 0.3 });
  },
  plane(k, { w, d, h, c }, g) {
    // a jet seen side on through the window: fuselage, wing, tail and engines
    const r = h * 0.18;
    const body = k.cylinder(r, w * 0.85, '#f4f6f8', 0, h * 0.4, 0, g, r, 14);
    body.rotation.z = Math.PI / 2;
    const nose = k.cone(r, w * 0.12, '#f4f6f8', w * 0.485, h * 0.4, 0, g, 14, 0);
    nose.rotation.z = -Math.PI / 2;
    k.box(w * 0.85, h * 0.05, 0.02, c, 0, h * 0.45, r * 0.98, g); // the airline stripe
    row(k, 14, w * 0.6, h * 0.48, r * 0.99, g, ['#2b3238'], { w: 0.12, h: 0.12, d: 0.02 });
    const wing = k.box(w * 0.25, 0.08, d * 0.9, '#d9dee1', -w * 0.05, h * 0.32, 0, g);
    wing.rotation.y = 0.35;
    k.box(w * 0.12, h * 0.38, 0.1, c, -w * 0.4, h * 0.65, 0, g); // tail fin
    k.cylinder(r * 0.4, w * 0.12, '#c9cfd3', -w * 0.02, h * 0.22, d * 0.25, g).rotation.z = Math.PI / 2;
    for (const x of [-0.3, 0.25]) k.box(0.08, h * 0.25, 0.08, '#3d4650', x * w, h * 0.12, 0, g);
  },
  cablestation(k, { w, d, h, c }, g) {
    // the station: a deck, the big wheel the cable turns round, and the steel roof over it
    k.box(w, 0.3, d, '#c9cfd3', 0, 0.15, 0, g);
    for (const x of [-1, 1]) for (const z of [-1, 1]) k.box(0.15, h, 0.15, c, (x * w) / 2.2, h / 2, (z * d) / 2.2, g);
    k.box(w * 1.05, 0.15, d * 1.05, c, 0, h, 0, g);
    k.cylinder(d * 0.35, 0.12, '#3d4650', 0, h - 0.5, 0, g, d * 0.35, 24);
    k.box(w * 1.6, 0.04, 0.04, '#2a2d31', w * 0.3, h - 0.5, -d * 0.3, g);
    k.box(w * 1.6, 0.04, 0.04, '#2a2d31', w * 0.3, h - 0.5, d * 0.3, g);
  },
  gondola(k, { w, d, h, c }, g) {
    // a cable car cabin hanging from its arm
    k.box(w, h * 0.65, d, c, 0, h * 0.35, 0, g);
    k.box(w * 1.01, h * 0.3, d * 1.01, '#9fc7dc', 0, h * 0.45, 0, g);
    k.box(0.06, h * 0.35, 0.06, '#3d4650', 0, h * 0.85, 0, g);
    k.box(0.4, 0.1, 0.12, '#3d4650', 0, h, 0, g);
  },
  pylon(k, { w, h, c }, g) {
    for (const x of [-1, 1]) {
      const leg = k.box(0.15, h, 0.15, c, (x * w) / 3, h / 2, 0, g);
      leg.rotation.z = x * 0.08;
    }
    for (let y = 1; y < h; y += 1.2) k.box(w * 0.5, 0.06, 0.06, c, 0, y, 0, g);
    k.box(w, 0.15, 0.4, c, 0, h, 0, g);
  },
  clipper(k, { w, d, h, c, label }, g) {
    // a Thames river boat moored alongside: hull, cabin of windows, the open deck at the back
    k.box(w, h * 0.35, d, '#f4f6f8', 0, h * 0.12, 0, g);
    k.box(w, h * 0.08, d * 1.01, c, 0, h * 0.28, 0, g);
    const bow = k.cone(d / 2, w * 0.15, '#f4f6f8', w / 2 + w * 0.06, h * 0.12, 0, g, 4, Math.PI / 4);
    bow.rotation.z = -Math.PI / 2;
    k.box(w * 0.6, h * 0.35, d * 0.85, '#e9ecef', -w * 0.05, h * 0.5, 0, g);
    row(k, 8, w * 0.55, h * 0.4, d * 0.43, g, ['#2b3238'], { w: 0.3, h: 0.2, d: 0.02 });
    k.box(w * 0.62, 0.06, d * 0.88, '#1f2937', -w * 0.05, h * 0.69, 0, g);
    if (label) k.sign(label, -w * 0.05, h * 0.6, d * 0.44, g, Math.min(w * 0.45, 2.4), '#fffdf5', c);
  },
  pierdeck(k, { w, d, c }, g) {
    k.box(w, 0.12, d, c, 0, 0.06, 0, g);
    for (let i = 0; i < Math.round(w / 0.25); i += 1)
      k.box(0.02, 0.005, d, dark(c, 0.8), -w / 2 + i * 0.25, 0.122, 0, g);
    for (const x of [-1, 1])
      for (const z of [-1, 1]) k.cylinder(0.12, 0.6, dark(c, 0.6), (x * w) / 2.1, 0.1, (z * d) / 2.1, g);
  },
  forklift(k, { w, d, h, c }, g) {
    k.box(w * 0.6, h * 0.35, d, c, -w * 0.1, h * 0.25, 0, g);
    k.box(w * 0.4, 0.05, d, '#1f2429', -w * 0.15, h, 0, g); // the roof guard
    for (const x of [-1, 1])
      for (const z of [-1, 1])
        k.box(0.04, h * 0.6, 0.04, '#1f2429', -w * 0.15 + x * w * 0.18, h * 0.7, (z * d) / 2.2, g);
    k.box(0.1, h * 1.1, d * 0.6, '#3d4650', w * 0.25, h * 0.55, 0, g); // the mast
    for (const z of [-1, 1]) k.box(w * 0.4, 0.05, 0.1, '#3d4650', w * 0.45, 0.1, (z * d) / 4, g);
    for (const x of [-1, 1])
      for (const z of [-1, 1]) {
        const wheel = k.cylinder(0.15, 0.12, '#111418', -w * 0.1 + x * w * 0.22, 0.15, (z * d) / 2, g, 0.15, 10);
        wheel.rotation.x = Math.PI / 2;
      }
  },

  showcar(k, { w, d, h, c, label }, g) {
    // a car on the showroom floor, side on: the body, the cabin and its glass, the wheels, and the name on a
    // stand in front (w is its length, d its width)
    const r = Math.min(0.36, h * 0.24);
    k.box(w * 0.98, h * 0.42, d * 0.92, c, 0, r + h * 0.21, 0, g);
    k.box(w * 0.52, h * 0.36, d * 0.8, c, -w * 0.05, r + h * 0.6, 0, g);
    for (const z of [-1, 1]) k.box(w * 0.46, h * 0.26, 0.03, '#33414d', -w * 0.05, r + h * 0.62, (z * d) / 2.45, g);
    k.box(0.03, h * 0.26, d * 0.72, '#3a4a57', w * 0.215, r + h * 0.62, 0, g);
    k.box(0.03, h * 0.24, d * 0.72, '#3a4a57', -w * 0.32, r + h * 0.62, 0, g);
    for (const x of [-1, 1]) {
      k.lit(0.05, 0.14, d * 0.18, x > 0 ? '#fff4d0' : '#e0302a', (x * w) / 2, r + h * 0.28, d * 0.3, g, 1.2);
      k.lit(0.05, 0.14, d * 0.18, x > 0 ? '#fff4d0' : '#e0302a', (x * w) / 2, r + h * 0.28, -d * 0.3, g, 1.2);
    }
    for (const x of [-1, 1])
      for (const z of [-1, 1]) {
        const wheel = k.cylinder(r, 0.24, '#1c1f24', x * w * 0.31, r, (z * d) / 2.1, g, r, 14);
        wheel.rotation.x = Math.PI / 2;
        const hub = k.cylinder(r * 0.45, 0.26, '#9aa3ab', x * w * 0.31, r, (z * d) / 2.1, g, r * 0.45, 10);
        hub.rotation.x = Math.PI / 2;
      }
    if (label) {
      k.box(0.06, 0.7, 0.06, '#3d4650', 0, 0.35, d / 2 + 0.5, g);
      k.sign(label, 0, 0.85, d / 2 + 0.5, g, Math.min(w * 0.5, 1.6), '#fffdf5', '#111827');
    }
  },

  // ---------- industry and land
  tanks(k, { w, d, h, c }, g) {
    const count = Math.max(1, Math.round(w / Math.max(1, d)));
    const r = Math.min(d, w / count) * 0.45;
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / count;
      k.cylinder(r, h, c, x, h / 2, 0, g, r, 16);
      k.dome(r, light(c, 0.2), x, h, 0, g);
      for (const y of [0.3, 0.6]) k.cylinder(r + 0.02, 0.05, dark(c, 0.8), x, h * y, 0, g, r + 0.02, 16);
      k.box(0.06, h, 0.06, '#facc15', x + r, h / 2, 0, g);
    }
    k.box(w, 0.1, 0.1, '#7d8790', 0, h * 0.85, 0, g);
  },
  conveyor(k, { w, d, h, c }, g) {
    k.box(w, 0.08, d, '#1f2429', 0, h, 0, g);
    k.box(w, 0.15, d + 0.08, c, 0, h - 0.1, 0, g);
    legs(k, w, d, h - 0.15, '#4b5563', g, 0.08);
    row(k, Math.round(w * 1.5), w * 0.9, h + 0.04, 0, g, ['#fffdf5', '#e7dcc7'], { w: 0.4, h: 0.22, d: d * 0.6 });
  },
  pallets(k, { w, d, h, c, label }, g) {
    // sacks stacked on wooden pallets (sugar at the refinery)
    k.box(w, 0.14, d, '#b07d4a', 0, 0.07, 0, g);
    const layers = Math.max(1, Math.round((h - 0.14) / 0.25));
    for (let l = 0; l < layers; l += 1)
      for (let i = 0; i < 2; i += 1)
        for (let j = 0; j < 2; j += 1)
          k.box(
            w / 2 - 0.04,
            0.23,
            d / 2 - 0.04,
            l % 2 ? light(c, 0.2) : c,
            -w / 4 + (i * w) / 2,
            0.14 + 0.12 + l * 0.25,
            -d / 4 + (j * d) / 2,
            g,
          );
    if (label) k.sign(label, 0, 0.14 + layers * 0.25 - 0.15, d / 2 + 0.01, g, w * 0.7, '#162e3a', '#fffdf5');
  },
  controlpanel(k, { w, d, h, c }, g) {
    k.box(w, h * 0.6, d, c, 0, (h * 0.6) / 2, 0, g);
    const top = k.box(w, 0.05, d * 0.8, '#1f2429', 0, h * 0.62, 0.05, g);
    top.rotation.x = 0.4;
    for (let i = 0; i < Math.round(w * 4); i += 1)
      k.lit(
        0.06,
        0.03,
        0.06,
        ['#22c55e', '#e63946', '#f59e0b'][i % 3]!,
        -w / 2 + 0.15 + i * 0.25,
        h * 0.66,
        0.05,
        g,
        1.2,
      );
    k.box(w * 0.9, h * 0.4, 0.04, '#1f2429', 0, h * 0.85, -d / 2 + 0.02, g);
    k.lit(w * 0.84, h * 0.34, 0.01, '#2f6fa3', 0, h * 0.85, -d / 2 + 0.045, g, 0.6);
  },
  plot(k, { w, d, c, label }, g) {
    // a plot of earth behind a low fence, with the For Sale board
    k.box(w, 0.04, d, '#8a6a4a', 0, 0.02, 0, g);
    for (let i = 0; i < Math.round(w * d * 0.8); i += 1)
      k.box(
        0.2,
        0.02,
        0.2,
        '#6f8a4a',
        -w / 2 + 0.2 + ((i * 0.73) % (w - 0.4)),
        0.045,
        -d / 2 + 0.2 + ((i * 1.19) % (d - 0.4)),
        g,
      );
    for (const [x, z, long, across] of [
      [0, -d / 2, w, true],
      [0, d / 2, w, true],
      [-w / 2, 0, d, false],
      [w / 2, 0, d, false],
    ] as const) {
      k.box(across ? long : 0.04, 0.05, across ? 0.04 : long, c, x, 0.45, z, g);
      for (let i = 0; i <= Math.round(long / 0.8); i += 1) {
        const at = -long / 2 + (long * i) / Math.round(long / 0.8);
        k.box(0.06, 0.55, 0.06, dark(c, 0.8), across ? at : x, 0.27, across ? z : at, g);
      }
    }
    k.box(0.06, 1.6, 0.06, '#5b4636', w * 0.3, 0.8, d / 2 - 0.1, g);
    k.box(1.1, 0.6, 0.04, '#fffdf5', w * 0.3, 1.45, d / 2 - 0.07, g);
    k.sign(label ?? 'FOR SALE', w * 0.3, 1.45, d / 2 - 0.045, g, 1, '#fffdf5', '#c81e1e');
  },
  portacabin(k, { w, d, h, c, label }, g) {
    k.box(w, h, d, c, 0, h / 2 + 0.15, 0, g);
    for (const x of [-1, 1]) k.box(0.2, 0.15, d, '#4b5563', (x * w) / 2.4, 0.075, 0, g);
    k.box(0.85, 1.9, 0.04, dark(c, 0.7), -w * 0.3, 1.1, d / 2 + 0.01, g);
    k.box(1.2, 0.7, 0.04, '#cfe8f2', w * 0.15, 1.5, d / 2 + 0.01, g);
    k.box(0.9, 0.3, 0.6, '#7d8790', -w * 0.3, 0.1, d / 2 + 0.3, g);
    if (label) k.sign(label, w * 0.1, h - 0.1, d / 2 + 0.03, g, Math.min(w * 0.6, 2.2), '#fffdf5', dark(c, 0.55));
  },
  billboard(k, { w, h, c, label }, g) {
    // a board on two posts (or hung on a wall when it is low)
    const board = Math.min(h * 0.55, w * 0.5);
    for (const x of [-1, 1]) k.box(0.12, h - board, 0.12, '#4b5563', (x * w) / 3, (h - board) / 2, -0.1, g);
    k.box(w + 0.15, board + 0.15, 0.12, '#1f2429', 0, h - board / 2, -0.05, g);
    k.lit(w, board, 0.02, c, 0, h - board / 2, 0.02, g, 0.5);
    if (label) k.sign(label, 0, h - board / 2, 0.04, g, w * 0.85, '#fffdf5', c);
  },
  ship(k, { w, d, h, c, label }, g) {
    // a tall ship (the Cutty Sark): the black hull with its gold line, three masts with the rigging, raised on
    // the glass base it sits on
    k.glass(w * 0.9, 0.5, d * 1.15, 0, 0.25, 0, g); // the glass canopy round the dry dock
    k.box(w * 0.8, h * 0.18, d, c, 0, 0.5 + (h * 0.18) / 2, 0, g);
    k.box(w * 0.8, h * 0.02, d * 1.01, '#c9a227', 0, 0.5 + h * 0.17, 0, g);
    const bow = k.cone(d / 2, w * 0.18, c, w * 0.49, 0.5 + (h * 0.18) / 2, 0, g, 4, Math.PI / 4);
    bow.rotation.z = -Math.PI / 2;
    k.box(w * 0.82, 0.05, d * 0.9, '#b07d4a', 0, 0.5 + h * 0.18, 0, g); // the deck
    for (const x of [-0.28, 0, 0.26]) {
      const mastH = h * (x === 0 ? 0.82 : 0.72);
      k.cylinder(0.08, mastH, '#5b3a29', x * w, 0.5 + h * 0.18 + mastH / 2, 0, g, 0.05);
      for (const y of [0.35, 0.6, 0.82])
        k.box(0.06, 0.06, d * 1.3 * (1.1 - y), '#5b3a29', x * w, 0.5 + h * 0.18 + mastH * y, 0, g);
      k.box(0.02, mastH * 0.9, 0.02, '#3d2a1e', x * w + 0.3, 0.5 + h * 0.18 + mastH * 0.45, 0, g, 0.3);
    }
    k.box(w * 0.75, 0.02, 0.02, '#3d2a1e', 0, 0.5 + h * 0.7, 0, g); // a stay between the masts
    if (label) k.sign(label, -w * 0.15, 0.5 + h * 0.09, d / 2 + 0.01, g, Math.min(w * 0.4, 2.4), '#c9a227', c);
  },
};
